import { useEffect, useRef, useState } from "react";
import moment from "moment";
import { makeStyles } from "tss-react/mui";
import {
  MiButton,
  MiDatePicker,
  MiIcon,
  MiIconButton,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  DATEPICKER_VARIANTS,
  DATE_FORMAT,
} from "@miplatform/constants";
import { Colors } from "@miplatform/types";
import { getDate, isToday } from "@miplatform/utils";
import { mdiCalendarRefresh, mdiChevronLeft, mdiChevronRight } from "@mdi/js";
import { Box, useTheme } from "@mui/material";

/**
 * A custom date picker component that provides date selection with navigation controls.
 *
 * @param {Object} props - The component props
 * @param {Colors} [props.color=Colors.Primary] - The color of the date picker
 * @param {moment.Moment} [props.date=moment()] - The currently selected date as a moment object
 * @param {Function} [props.setDate=() => void] - Callback function to update the selected date
 * @param {Function} [props.onDateChange] - Callback function triggered when the date changes (debounced)
 * @param {boolean} [props.disabled=false] - Whether the date picker is disabled
 * @param {Function} [props.shouldDisableDate] - Function to determine if a date should be disabled
 * @param {boolean} [props.hideTodayButton=false] - Whether to hide the "Today" button
 *
 * @returns {JSX.Element} A date picker component with navigation buttons and a "Today" button
 */
export function DatePicker({
  date = getDate(),
  setDate = () => null,
  color = Colors.Primary,
  onDateChange,
  disabled = false,
  shouldDisableDate,
  hideTodayButton = false,
}) {
  const timeoutRef = useRef(null);
  const { classes } = useStyles();
  const theme = useTheme();

  const [open, setOpen] = useState(false);

  const closeCalendar = () => {
    setOpen(false);
  };

  const openCalendar = () => {
    setOpen(true);
  };

  const handleDateDebounceChange = (date) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      onDateChange?.(date);
      timeoutRef.current = null;
    }, 300);
  };

  const handleDateChange = (date) => {
    setDate(date);
    handleDateDebounceChange(date);
  };

  useEffect(() => {
    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, []);

  return (
    <Box className={classes.container} data-testid="date-picker">
      <MiDatePicker
        views={["month", "day"]}
        value={date}
        format={DATE_FORMAT}
        open={open}
        color={color}
        onOpen={openCalendar}
        onClose={closeCalendar}
        onMonthChange={openCalendar}
        onViewChange={openCalendar}
        disabled={disabled}
        onChange={handleDateChange}
        variant={DATEPICKER_VARIANTS.DESKTOP}
        shouldDisableDate={shouldDisableDate}
        slotProps={{
          day: {
            onBlur: closeCalendar,
          },
          textField: {
            size: "small",
            sx: {
              "& input": {
                padding: 0,
              },
              "& .MuiOutlinedInput-root": {
                padding: 0,
                color: theme.palette[color].main,
                "& fieldset": {
                  border: "none",
                },
              },
            },
            inputProps: {
              style: {
                textAlign: "center",
                cursor: "pointer",
                color: theme.palette[color].main,
              },
              onClick: openCalendar,
              "data-testid": "date-picker-main",
            },
            readOnly: true,
            InputProps: {
              startAdornment: (
                <MiIconButton
                  disabled={
                    disabled ||
                    shouldDisableDate?.(date.clone().subtract(1, "days"))
                  }
                  onClick={() => {
                    handleDateChange(date.clone().subtract(1, "days"));
                  }}
                  className={classes.button}
                  data-testid="back-button"
                >
                  <MiIcon
                    path={mdiChevronLeft}
                    size={0.85}
                    color={
                      disabled
                        ? theme.palette.action.disabled
                        : theme.palette.primary.main
                    }
                  />
                </MiIconButton>
              ),
              endAdornment: (
                <MiIconButton
                  disabled={
                    disabled || shouldDisableDate?.(date.clone().add(1, "days"))
                  }
                  onClick={() => {
                    handleDateChange(date.clone().add(1, "days"));
                  }}
                  className={classes.button}
                  data-testid="forward-button"
                >
                  <MiIcon
                    path={mdiChevronRight}
                    size={0.85}
                    color={
                      disabled
                        ? theme.palette.action.disabled
                        : theme.palette.primary.main
                    }
                  />
                </MiIconButton>
              ),
              style: { padding: 0 },
            },
          },
          previousIconButton: { style: { marginRight: 0 } },
          nextIconButton: { style: { marginLeft: 0 } },
          field: { shouldRespectLeadingZeros: true },
        }}
      />
      {!hideTodayButton && (
        <MiButton
          size="small"
          title="Today"
          variant={BUTTON_VARIANTS.CONTAINED}
          color={BUTTON_COLORS.PRIMARY}
          startIcon={{
            path: mdiCalendarRefresh,
          }}
          onClick={() => {
            handleDateChange(moment());
          }}
          disabled={isToday(date) || disabled || shouldDisableDate?.(moment())}
          disableRipple
          data-testid="today-button"
        />
      )}
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  container: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    flex: "0 0 auto",
  },
  button: {
    padding: theme.spacing(0),
  },
}));
